import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { IAbstractProductIncluded } from '../../../models/abstract-product.models';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { environment } from '../../../../environments/environment';
import { ArrayUtils } from '../../../utils/array.utils';
import { ProductUtils } from '../../../utils/product.utils';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { IContractParams } from '../../../models/catalog.models';
import { Router } from '@angular/router';
import { CpqUtils } from '../../../utils/cpq.utils';

@Component({
  selector: 'app-recommendations',
  templateUrl: './recommendations.component.html',
  styleUrls: ['./recommendations.component.scss'],
  standalone: false,
})
export class RecommendationsComponent implements OnInit, OnDestroy {
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly router: Router = inject(Router);

  isLoaded: boolean = false;
  infoItems: {
    key: string;
    value: any;
    options: any[];
  }[] = [];
  abstractProducts: IAbstractProductIncluded[] = [];
  addItemInProgress: boolean = false;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  @Input() recommendations!: Array<string>;
  @Output() startOver: EventEmitter<void> = new EventEmitter<void>();
  @Output() goBackOneStep: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectedContract: EventEmitter<string> = new EventEmitter<string>();
  @Input() contractParams!: IContractParams;

  ngOnInit(): void {
    forkJoin(
      this.recommendations.map(sku => this.catalogFacade.getAbstractProductDataFromApi(`A_${sku}`)),
    ).pipe(takeUntil(this.unsubscribe$))
      .subscribe({
          next: ([...args]) => {
            this.abstractProducts = args.map(arg => arg.data);
          },
          complete: () => {
            this.isLoaded = true;
          },
          error: () => {
            this.isLoaded = true;
            this.abstractProducts = ArrayUtils.sortByAttribute(
              [...this.abstractProducts], 'attributes.attributes.expensiveness');
            this.getCpqItemsAttributesKeyAndValue();
          },
        },
      );
    this.selectAddItemInProgress();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  hasTranslation(key: string): boolean {
    const translation: string = this.translate.instant(key);
    return !!translation && translation !== key;
  }

  getCpqItemsAttributes(product: IAbstractProductIncluded): string[] {
    return (product.attributes.superAttributesDefinition ?? []).filter((attribute: string): boolean =>
      !environment.cpqItemAttributesToHide.includes(attribute),
    );
  }

  getCpqItemsAttributesKeyAndValue(): void {
    this.infoItems = this.abstractProducts.reduce<{
      key: string;
      value: any;
      options: any[];
    }[]>((acc, product: IAbstractProductIncluded) => {
      const attributes: {
        key: string;
        value: any;
        options: any[];
      }[] = this.getCpqItemsAttributes(product).reduce<{
        key: string;
        value: any;
        options: any[];
      }[]>((attributesAcc, attribute: string) => {
        const options: any[] = this.abstractProducts.map((item: IAbstractProductIncluded): any =>
          Object.entries(item.attributes.attributes ?? {})
            .find(([key]: [string, any]): boolean => key === attribute)?.[1]);
        const value: any = Object.entries(product.attributes.attributeNames ?? {})
          .find(([key]: [string, any]): boolean => key === attribute)?.[1] ?? '';
        attributesAcc.push({
          key: attribute,
          value,
          options,
        });
        return attributesAcc;
      }, []);
      const filteredAttributes: {
        key: string;
        value: any;
        options: any[];
      }[] = attributes.reduce<{
        key: string;
        value: any;
        options: any[];
      }[]>((optionsAcc, item) => {
        const options: any[] = item.options.filter((option: any): boolean => option !== '' && option !== null);
        if (options.length > 0) {
          optionsAcc.push({
            key: item.key,
            value: item.value,
            options,
          });
        }
        return optionsAcc;
      }, []);
      const filteredAttributesWithOptions: {
        key: string;
        value: any;
        options: any[];
      }[] = filteredAttributes.filter((element: {key: string; value: any; options: any[]}): boolean =>
        !acc.find((elem: {key: string; value: any; options: any[]}): boolean => elem.key === element.key));
      acc.push(...filteredAttributesWithOptions);
      return acc;
    }, []);
  }

  getDetailAttribute(attributeValue: string | null): string {
    return ProductUtils.getDetailAttribute(attributeValue);
  }

  goBackToStart(): void {
    this.startOver.emit();
  }

  selectContract(contractSku: string): void {
    this.selectedContract.emit(contractSku);
  }

  contactUs(product: IAbstractProductIncluded): void {
    const url: string = this.router.createUrlTree(
      ['/contact-us'],
      {queryParams: this.getExtendedContractParams(product)},
    ).toString();

    this.router.navigateByUrl(url).then();
  }

  getExtendedContractParams(product: IAbstractProductIncluded): IContractParams {
    return {
      ...CpqUtils.filterOutEmptyParams(this.contractParams),
      selectedService: product.attributes.name,
    };
  }

  private selectAddItemInProgress(): void {
    this.marketingFacade.isAddItemOperationInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(addItemInProgress => {
        this.addItemInProgress = addItemInProgress;
      },
    );
  }
}
